import { SocialSharingActions } from '@rocco/ui/social-sharing/components/SocialSharingActions';
import { ClientOnly } from 'remix-utils';

import {
    PLATFORMS,
    type Platform,
} from '@rocco/ui/social-sharing/utils/platform';
import type { DisplayMode } from '@rocco/types/enum/display-mode';
import {
    shareNative,
    shareContent,
    type ShareOptions,
    isNativeShareAvailable,
} from '../utils/sharing';
import { useTracker } from '@archipro-website/tracker';
import { capitalizeFirstLetter } from '../../../utils/stringHelper';
import { useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { uiStateAtomSocialSharingModalIsOpened } from '@rocco/ui/social-sharing/components/SocialSharingModal';
import { shareTargetAtom } from '../state/share-target';
interface SocialShareInitializerProps {
    displayMode: DisplayMode;
}

export const SocialShareInitializer = ({
    displayMode,
}: SocialShareInitializerProps) => {
    const tracker = useTracker();
    const shareTarget = useAtomValue(shareTargetAtom);
    const setShareTarget = useSetAtom(shareTargetAtom);
    const shareModalOpen = useAtomValue(uiStateAtomSocialSharingModalIsOpened);

    // Clear any per-item share override once the modal closes.
    useEffect(() => {
        if (!shareModalOpen) setShareTarget(null);
    }, [shareModalOpen, setShareTarget]);

    // Open Graph metadata from the page's own meta tags.
    const getMetaContent = (property: string): string => {
        if (typeof document === 'undefined') return '';
        const metaTag =
            document.querySelector(`meta[property="${property}"]`) ||
            document.querySelector(`meta[name="${property}"]`);
        return metaTag ? metaTag.getAttribute('content') || '' : '';
    };

    const onShare = (platform: Platform) => {
        // A per-item override (e.g. a listing tile) wins; otherwise share the
        // current page via Open Graph metadata, falling back to the page URL.
        const opts: ShareOptions = shareTarget ?? {
            url: getMetaContent('og:url') || window.location.href,
            title: getMetaContent('og:title') || window.document.title,
            description:
                getMetaContent('og:description') ||
                getMetaContent('description'),
            // Pinterest pins a picture rather than a page, and refuses without
            // one. The page's own Open Graph image is what it would show.
            image: getMetaContent('og:image') || undefined,
        };

        // The chosen target is honoured on every device. Touch used to hand
        // the whole sheet to the OS one instead, so a visitor who tapped
        // Pinterest — or Copy link — got the phone's share tray and whatever
        // it happened to offer.
        void shareContent(platform, opts).then((result) => {
            if (result.success || !isNativeShareAvailable()) return;
            // A target that could not open at all (a blocked popup, an app
            // that is not installed) still leaves the visitor with the OS
            // sheet rather than nothing.
            void shareNative(opts);
            tracker.log('ShareSubmit', {
                url: new URL(window.location.href),
                data: {
                    ExtraData: 'NativeShare',
                },
            });
        });
    };

    // Pinterest pins a picture, so it is only offered when there is one. The
    // tile's own sheet decides the same way; without this the site-wide sheet
    // would show a target that could do nothing on a page with no image.
    const shareImage = shareTarget?.image || getMetaContent('og:image');
    const platforms: Platform[] = shareImage
        ? [...PLATFORMS]
        : PLATFORMS.filter((platform) => platform !== 'pinterest');

    return (
        <ClientOnly>
            {() => (
                <>
                    <SocialSharingActions
                        displayMode={displayMode}
                        platforms={platforms}
                        onShare={onShare}
                        trackingConfig={{
                            onOpen: () => {
                                tracker.log('ShareReveal', {
                                    url: new URL(window.location.href),
                                });
                            },
                            onShare: (platform) => {
                                tracker.log('ShareSubmit', {
                                    url: new URL(window.location.href),
                                    data: {
                                        ExtraData:
                                            capitalizeFirstLetter(platform),
                                    },
                                });
                            },
                        }}
                    />
                </>
            )}
        </ClientOnly>
    );
};
